import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '@/hooks/use-toast';
import { fireEvent } from '@/lib/analytics';
import { postToZoho } from '@/lib/zoho';
import { readLeadContext, validateLeadContext, type LeadContext } from '@/lib/leadContext';
import PrefilledBadge from './PrefilledBadge';
import IntentTextarea from './IntentTextarea';
import EmailInput from './EmailInput';

interface CtaFormData {
  email: string;
  name?: string;
  intentMessage: string;
  timeframe?: string;
  budget?: string;
  consent: boolean;
  honeypot: string;
}

interface CtaFormProps {
  pageType: 'free' | 'offer' | 'bin' | 'lease';
  subjectPrefix: string;
  onSuccess?: () => void;
  className?: string;
}

interface ValidationErrors {
  email?: string;
  intentMessage?: string;
  consent?: string;
  domain?: string;
}

export default function CtaForm({
  pageType,
  subjectPrefix,
  onSuccess,
  className = '',
}: CtaFormProps) {
  const location = useLocation();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [contextError, setContextError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CtaFormData>({
    email: '',
    name: '',
    intentMessage: '',
    timeframe: '',
    budget: '',
    consent: false,
    honeypot: '',
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Parse lead context on mount
  useEffect(() => {
    const context = readLeadContext(location);
    const error = validateLeadContext(context);

    setLeadContext(context);
    setContextError(error);

    if (context.domain && !error) {
      fireEvent('cta_page_view', { type: pageType, domain: context.domain });
    }
  }, [location, pageType]);

  const validateForm = (): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.intentMessage.trim()) {
      errors.intentMessage = 'Please describe what you plan to build';
    } else if (formData.intentMessage.trim().length < 50) {
      errors.intentMessage = 'Please provide more detail (at least 50 characters)';
    }

    if (!formData.consent) {
      errors.consent = 'Please confirm we may contact you about this request';
    }

    if (contextError) {
      errors.domain = contextError;
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check honeypot
    if (formData.honeypot) {
      return;
    }

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (!leadContext || !leadContext.domain) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare payloads
      const basePayload = {
        domain: leadContext.domain,
        email: formData.email,
        name: formData.name || '',
        intentMessage: formData.intentMessage,
        timeframe: formData.timeframe || '',
        budget: formData.budget || '',
        pageType,
        origin_page: leadContext.origin_page,
        referrer: leadContext.referrer,
        utm_source: leadContext.utm_source,
        utm_medium: leadContext.utm_medium,
        utm_campaign: leadContext.utm_campaign,
        utm_term: leadContext.utm_term,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        subject: `${subjectPrefix} ${leadContext.domain}`,
      };

      // Submit to existing backend (Resend)
      const backendResponse = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(basePayload),
      });

      if (!backendResponse.ok) {
        throw new Error('Backend submission failed');
      }

      // Submit to Zoho Flow (non-blocking)
      postToZoho({
        ...basePayload,
        // Additional Zoho-specific fields
        deal_name: `${leadContext.domain} • ${leadContext.origin_page}`,
        pipeline: 'Attain Domains',
        stage: 'New',
        acquisition_channel: leadContext.referrer ? 'Referral' : 'Direct',
      }).catch((error) => {
        console.error('Zoho submission failed (non-blocking):', error);
      });

      fireEvent('cta_submit', { type: pageType, domain: leadContext.domain });

      toast({
        title: 'Submission successful!',
        description: getSuccessMessage(pageType),
      });

      onSuccess?.();
    } catch (error) {
      console.error('Form submission error:', error);
      fireEvent('cta_submit_fail', {
        type: pageType,
        domain: leadContext?.domain,
        code: 'api_error',
      });

      toast({
        title: 'Submission failed',
        description: 'Please try again or contact me directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSuccessMessage = (type: string): string => {
    switch (type) {
      case 'free':
        return "I'll review your application and get back to you within 48 hours.";
      case 'lease':
        return "I'll send you lease terms and next steps within 24 hours.";
      case 'offer':
        return "I'll review your offer and respond within 24-48 hours.";
      case 'bin':
        return "I've received your inquiry and will follow up shortly.";
      default:
        return "I'll be in touch soon!";
    }
  };

  // Show domain error if context is invalid
  if (contextError) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive text-sm font-medium">{contextError}</p>
          <p className="text-destructive/80 text-xs mt-1">
            Please ensure you accessed this page from a valid domain listing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {leadContext?.domain && <PrefilledBadge domain={leadContext.domain} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot field */}
        <input
          type="text"
          name="website"
          value={formData.honeypot}
          onChange={(e) => setFormData((prev) => ({ ...prev, honeypot: e.target.value }))}
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />

        <EmailInput
          email={formData.email}
          onEmailChange={(email) => setFormData((prev) => ({ ...prev, email }))}
          consent={formData.consent}
          onConsentChange={(consent) => setFormData((prev) => ({ ...prev, consent }))}
          emailError={errors.email}
        />

        <IntentTextarea
          value={formData.intentMessage}
          onChange={(intentMessage) => setFormData((prev) => ({ ...prev, intentMessage }))}
          error={errors.intentMessage}
        />

        {errors.consent && <p className="text-xs text-destructive">{errors.consent}</p>}

        <Button type="submit" className="w-full" disabled={isSubmitting} size="lg">
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            getSubmitText(pageType)
          )}
        </Button>
      </form>
    </div>
  );
}

function getSubmitText(pageType: string): string {
  switch (pageType) {
    case 'free':
      return 'Apply to Use for Free';
    case 'lease':
      return 'Request Lease Terms';
    case 'offer':
      return 'Submit Offer';
    case 'bin':
      return 'Express Interest';
    default:
      return 'Submit';
  }
}
